import moment from 'moment';
import { ToastMessageStatus } from '../codegen/graphql';

export interface DisplayableToastMessage {
    fromDate: string;
    toDate: string;
    status: string;
}

/**
 * Precise client-side visibility check. The server filter is deliberately
 * coarse (whole-day bounds, see below), and messages can expire or start
 * between polls — so time-of-day gating has to happen here against the
 * current instant. Both bounds inclusive.
 */
export function isToastMessageCurrentlyDisplayed(
    message: DisplayableToastMessage | null | undefined,
    now?: moment.MomentInput
): boolean {
    if (!message) return false;
    if (message.status !== ToastMessageStatus.ACTIVE) return false;
    const current = moment(now);
    return (
        current.isSameOrAfter(moment(message.fromDate)) &&
        current.isSameOrBefore(moment(message.toDate))
    );
}

/**
 * Earliest upcoming visibility boundary (a fromDate or toDate in the future)
 * across the given messages, or null if there is none. Drives precise
 * client-side re-evaluation: polling alone doesn't help because Apollo keeps
 * the same data reference for identical results, so a boundary passing
 * mid-poll-window would never re-trigger the display logic.
 */
export function nextVisibilityBoundary(
    messages: (DisplayableToastMessage | null | undefined)[] | null | undefined,
    now?: moment.MomentInput
): moment.Moment | null {
    const current = moment(now);
    let next: moment.Moment | null = null;
    (messages ?? []).forEach(message => {
        if (!message) return;
        [moment(message.fromDate), moment(message.toDate)].forEach(boundary => {
            if (!boundary.isValid() || !boundary.isAfter(current)) return;
            if (!next || boundary.isBefore(next)) next = boundary;
        });
    });
    return next;
}

/**
 * Coarse server-side filter for currently relevant messages. Bounds are
 * inclusive (DateTimeFilter) and deliberately whole-day — the value only
 * changes on day rollover, keeping poll variables stable; precise gating is
 * client-side. The directions read inverted:
 * fromDate ≤ today (before) and toDate ≥ today (after).
 */
export function buildActiveToastMessageFilter(now?: moment.MomentInput) {
    const today = moment(now).format('YYYY-MM-DD');
    return {
        status: { equals: ToastMessageStatus.ACTIVE },
        fromDate: { before: today },
        toDate: { after: today },
    };
}
